import type { User } from 'firebase/auth';
import { functionHeaders } from './functionSecurity';
import type { MessageAttachment } from './threads';
import type { EventPanelPhotoField } from './countdownEvents';

export type AttachmentWriteAction =
  | 'threadMessage'
  | 'threadMessageAttachmentAppend'
  | 'classPost'
  | 'classPostReply'
  | 'classPostReplyEdit'
  | 'classPostReplyRetract'
  | 'bagMessage'
  | 'bulletin'
  | 'offeringFamilyNote'
  | 'subRequest'
  | 'bagGroupItem'
  | 'awardNomination'
  | 'signupRequest'
  | 'shoutoutSubmission'
  | 'studentMilestone'
  | 'compBoardPost'
  | 'compBoardReply'
  | 'compTip'
  | 'countdownEventPanelPhoto'
  | 'countdownEventDelete'
  | 'jrStaffBoardThread'
  | 'jrStaffBoardReply'
  | 'jrStaffBoardEditThread'
  | 'jrStaffBoardEditReply'
  | 'parentBoardThread'
  | 'parentBoardReply'
  | 'parentBoardEditThread'
  | 'parentBoardEditReply'
  | 'jrStaffBoardCoverPhoto';

/** REFUTE_walls_R9 P3-4: the shared shape every onVideoMirrorBlocked
 * callback receives, structurally identical to
 * videoMirrorBlockedNotice.ts's own VideoMirrorBlockedDetail (declared
 * separately here so this wire-adapter module does not have to import a UI
 * copy module) - wasReplay (REFUTE_walls_R7 P3-4), and now how many of the
 * post's own video attachments did not land and whether that was a PARTIAL
 * block (some landed) rather than a TOTAL one (nothing did). */
export interface VideoMirrorBlockedDetail {
  wasReplay: boolean;
  blockedCount: number;
  partial: boolean;
}

export interface AttachmentWriteResponse {
  ok?: boolean;
  error?: string;
  messageId?: string;
  postId?: string;
  replyId?: string;
  bulletinId?: string;
  recipientUids?: string[];
  mentionedUids?: string[];
  expectedRecipientUids?: string[];
  /** Durable class-post alert operation accepted by the write gateway. */
  notificationOperationId?: string | null;
  memberUids?: string[];
  authorRole?: 'family' | 'director' | 'staff';
  postAuthorUid?: string | null;
  offeringId?: string | null;
  stampMyAck?: boolean;
  created?: boolean;
  requestId?: string;
  itemId?: string;
  nominationId?: string;
  tier?: string;
  eventId?: string;
  panel?: EventPanelPhotoField;
  attachment?: MessageAttachment;
  attachmentId?: string;
  attached?: boolean;
  removed?: boolean;
  deleted?: boolean;
  coverImage?: MessageAttachment;
  /** REFUTE_walls_R2 finding 2: set true when a class-board post carried a
   * video but that class's Music & videos already held 200 entries, so the
   * post itself went out fine but its video never reached the shelf. The
   * post is on the board either way; this only says the shelf did not get
   * the clip. */
  videoMirrorBlocked?: boolean;
  /** REFUTE_walls_R9 P3-4: how many of the post's own video attachments did
   * NOT land - a POST-level count (one multi-clip post is one event), never
   * a bare boolean, so a caller can say how much did not fit. */
  videoMirrorBlockedCount?: number;
  /** REFUTE_walls_R9 P3-4: true when SOME of the post's video landed and
   * some did not (round ten's Codex P2-B fix made that possible) - false
   * means a TOTAL block, nothing from this post reached the shelf. A
   * partial block must never be worded like, or suggest reposting the way,
   * a total one does: the clip already there would land twice. */
  videoMirrorPartial?: boolean;
}

export function attachmentReservationIds(attachments: MessageAttachment[]): string[] {
  const ids = attachments.map((attachment) => attachment.id);
  if (ids.length < 1 || ids.length > 4 || new Set(ids).size !== ids.length
      || ids.some((id) => !/^[A-Za-z0-9_-]{12,80}$/.test(id))) {
    throw new Error('One attachment is no longer ready. Choose it again and retry.');
  }
  return ids;
}

interface MessageWriteInput {
  user: User;
  action: AttachmentWriteAction;
  targetId: string;
  writeId: string;
  payload: Record<string, unknown>;
  attachments: MessageAttachment[];
}

async function saveMessageWrite(
  input: MessageWriteInput,
  reservationIds: string[]
): Promise<AttachmentWriteResponse> {
  const response = await fetch('/.netlify/functions/messageAttachmentWrite', {
    method: 'POST',
    credentials: 'same-origin',
    cache: 'no-store',
    headers: await functionHeaders(input.user),
    body: JSON.stringify({
      action: input.action,
      targetId: input.targetId,
      writeId: input.writeId,
      input: {
        ...input.payload,
        reservationIds,
      },
    }),
  });
  const payload = await response.json().catch(() => ({})) as AttachmentWriteResponse;
  if (!response.ok || payload.error) {
    const isCountdownEventAction = input.action === 'countdownEventPanelPhoto'
      || input.action === 'countdownEventDelete';
    const messages: Record<string, string> = {
      not_allowed: 'Your current Hub access no longer allows this message.',
      // A dancer's own sign-in, on any of hers, reads her bag group only
      // (DECISIONS 123 item 17, packet BG-10).
      dancer_reads_only: 'Dancers can read their bag group but cannot post in it or change its list. A parent or the studio can.',
      not_found: isCountdownEventAction
        ? 'That competition date is no longer available.'
        : 'That conversation is no longer available.',
      bad_request: 'That post could not be sent as written. Try again.',
      attachment_not_ready: 'One attachment is no longer ready. Choose it again and retry.',
      attachment_already_used: 'One attachment was already sent somewhere else. Choose it again.',
      conflict: isCountdownEventAction
        ? 'That event photo changed or could not be removed safely. Refresh and try again.'
        : 'That message changed in another tab. Refresh and try again.',
      message_recalled: 'That message was recalled, so this file was not attached.',
      server_not_configured: 'The studio message server is not configured yet.',
      award_round_closed: 'Nominations for that month are closed, so this pick was not sent.',
    };
    const error = new Error(messages[payload.error ?? ''] ?? 'That message could not be sent.') as Error & {
      retryable?: boolean;
      // The raw gateway code, so a caller with its own sentence for one
      // outcome does not have to match on the human sentence above.
      code?: string;
    };
    error.code = payload.error ?? '';
    if (['message_recalled', 'not_allowed', 'dancer_reads_only', 'not_found', 'attachment_already_used', 'conflict', 'award_round_closed'].includes(payload.error ?? '')) {
      error.retryable = false;
    }
    throw error;
  }
  return payload;
}

export async function saveAttachmentMessage(input: MessageWriteInput): Promise<AttachmentWriteResponse> {
  return saveMessageWrite(input, attachmentReservationIds(input.attachments));
}

/** Bag posts and replies share one authenticated, idempotent server writer. */
export async function saveBagGroupMessage(input: Omit<MessageWriteInput, 'action'>) {
  const reservationIds = input.attachments.length > 0
    ? attachmentReservationIds(input.attachments)
    : [];
  return saveMessageWrite({ ...input, action: 'bagMessage' }, reservationIds);
}

/**
 * EVERY staff class post goes through the gateway now, including text-only
 * ones, and there is no browser-side create left.
 *
 * A class post's two most dangerous fields were validated in the rules by
 * type alone: expectedRecipientUids decides which families see it, and
 * authorName decides whose name is on it. Neither was bound to anything, so
 * one document written from a console could address every family in the
 * studio under the Director's name (audit 2026-09-04). The gateway has
 * always derived both from the offering's roster and the caller's live user
 * doc; it was simply unreachable for a post with no photo on it.
 *
 * Empty reservationIds is valid for this action and for classPostReply.
 * Every attachment-bearing action keeps the one-to-four reservation rule.
 */
export async function saveClassPostMessage(
  input: Omit<MessageWriteInput, 'action'>,
  options: { onVideoMirrorBlocked?: (detail: VideoMirrorBlockedDetail) => void } = {}
) {
  // REFUTE_walls_R5 finding 1 / REFUTE_walls_R6 P3-1: round seven's dev log
  // fired only when THIS call's own response actually carried the flag -
  // invisible whenever the class shelf had room, which is every ordinary
  // dev session and test run, so a new direct caller could ship the same
  // gap and never see a single line about it. A hint that only shows up on
  // a full shelf is not a guard.
  //
  // This is the STRUCTURAL guard instead, the same shape
  // useCreateClassPost's own guard uses (classPosts.ts, REFUTE_walls_R4
  // finding 3): thrown BEFORE the fetch, keyed only on whether attachments
  // were passed with no callback at all - never on whether the shelf
  // happens to be full this run. Safe to throw here, unlike a
  // post-response check would be: nothing has been written yet, so failing
  // this pre-flight check never turns a real write into a lie. Gated off
  // in production so a caller this check has not caught yet still posts.
  if (
    input.attachments.length > 0
    && typeof options.onVideoMirrorBlocked !== 'function'
    && process.env.NODE_ENV !== 'production'
  ) {
    throw new Error(
      'saveClassPostMessage: attachments were passed with no onVideoMirrorBlocked. Every '
      + 'caller that can post a video through this gateway must handle a full class shelf '
      + 'blocking it, even if this particular call site believes it never can.'
    );
  }
  const reservationIds = input.attachments.length > 0
    ? attachmentReservationIds(input.attachments)
    : [];
  const result = await saveMessageWrite({ ...input, action: 'classPost' }, reservationIds);
  // REFUTE_walls_R7 P3-4: wasReplay is true when this write landed on the
  // gateway's replay branch (created === false - the post already
  // existed) rather than a fresh write, so a caller's notice can phrase
  // the block in the past tense instead of claiming the shelf is full now.
  // REFUTE_walls_R9 P3-4: blockedCount and partial ride along too, so a
  // caller's notice can say how much of the post's video did not fit and
  // never suggest a repost when part of it already landed.
  if (result.videoMirrorBlocked === true) {
    options.onVideoMirrorBlocked?.({
      wasReplay: result.created === false,
      blockedCount: typeof result.videoMirrorBlockedCount === 'number' ? result.videoMirrorBlockedCount : 1,
      partial: result.videoMirrorPartial === true,
    });
  }
  return result;
}

/**
 * Rich text-only writes use the same authenticated server transaction as an
 * attachment write. Firestore Rules can bind a plain message preview to its
 * text, but cannot parse the rich document to derive visible preview words.
 */
export async function saveMessageWithOptionalAttachments(input: MessageWriteInput): Promise<AttachmentWriteResponse> {
  const reservationIds = input.attachments.length > 0
    ? attachmentReservationIds(input.attachments)
    : [];
  return saveMessageWrite(input, reservationIds);
}

/**
 * Class-post replies always use the authenticated, App-Check-protected write
 * gateway, including text-only replies. Empty reservationIds is valid only
 * for this action; every attachment-bearing action keeps the one-to-four
 * reservation requirement above.
 */
export async function saveClassPostReplyMessage(input: Omit<MessageWriteInput, 'action'>) {
  const reservationIds = input.attachments.length > 0
    ? attachmentReservationIds(input.attachments)
    : [];
  return saveMessageWrite({ ...input, action: 'classPostReply' }, reservationIds);
}

/**
 * Fix a typo in your own class-post reply, or take it back.
 *
 * Both go through the SAME gateway that created the reply, because reply
 * creates are `allow create: if false` and the rules cannot afford an author
 * update+delete pair (measured at +211 content bytes against 79 free, which
 * Firebase would refuse). The caller's verified uid is the author check, on
 * the server, where a client cannot argue with it.
 *
 * Retract, not delete: the reply keeps its row and reads as withdrawn, exactly
 * as a class post does.
 */
export async function editClassPostReply(input: {
  user: MessageWriteInput['user'];
  postId: string;
  replyId: string;
  text: string;
}) {
  return saveMessageWrite({
    user: input.user,
    action: 'classPostReplyEdit',
    targetId: input.postId,
    writeId: input.replyId,
    attachments: [],
    payload: { text: input.text },
  }, []);
}

export async function retractClassPostReply(input: {
  user: MessageWriteInput['user'];
  postId: string;
  replyId: string;
}) {
  return saveMessageWrite({
    user: input.user,
    action: 'classPostReplyRetract',
    targetId: input.postId,
    writeId: input.replyId,
    attachments: [],
    payload: {},
  }, []);
}

/**
 * Add or replace one event-panel photo. The expected id is the photo the
 * editor was looking at when the upload began, so a stale tab cannot replace
 * a newer photo. The server infers save versus remove only from whether this
 * request carries its one reservation.
 */
export async function saveCountdownEventPanelPhoto(input: {
  user: User;
  eventId: string;
  panel: EventPanelPhotoField;
  attachment: MessageAttachment;
  expectedPhotoId: string | null;
}): Promise<AttachmentWriteResponse> {
  const attachments = [input.attachment];
  return saveMessageWrite(
    {
      user: input.user,
      action: 'countdownEventPanelPhoto',
      targetId: input.eventId,
      writeId: input.panel,
      payload: { expectedPhotoId: input.expectedPhotoId },
      attachments,
    },
    attachmentReservationIds(attachments)
  );
}

/**
 * Set (or replace) the Jr Staff board's one cover photo. Staff only, in the
 * server transaction that actually writes jrStaffBoardSettings/current - see
 * that transaction's own comment for why this is deliberately simpler than
 * the panel-photo lane above.
 */
export async function saveJrStaffBoardCoverPhoto(input: {
  user: User;
  attachment: MessageAttachment;
}): Promise<AttachmentWriteResponse> {
  const attachments = [input.attachment];
  return saveMessageWrite(
    {
      user: input.user,
      action: 'jrStaffBoardCoverPhoto',
      targetId: 'current',
      writeId: 'cover',
      payload: {},
      attachments,
    },
    attachmentReservationIds(attachments)
  );
}

/** Remove only the saved photo. Panel text stays untouched. */
export async function removeCountdownEventPanelPhoto(input: {
  user: User;
  eventId: string;
  panel: EventPanelPhotoField;
  expectedPhotoId: string;
}): Promise<AttachmentWriteResponse> {
  return saveMessageWrite(
    {
      user: input.user,
      action: 'countdownEventPanelPhoto',
      targetId: input.eventId,
      writeId: input.panel,
      payload: { expectedPhotoId: input.expectedPhotoId },
      attachments: [],
    },
    []
  );
}

/** Delete an event and durably reclaim every panel photo it owns. */
export async function deleteCountdownEventWithPhotos(input: {
  user: User;
  eventId: string;
}): Promise<AttachmentWriteResponse> {
  return saveMessageWrite(
    {
      user: input.user,
      action: 'countdownEventDelete',
      targetId: input.eventId,
      writeId: 'delete',
      payload: {},
      attachments: [],
    },
    []
  );
}
